import { useUnit } from 'effector-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createInstrumentFx } from '../../entities/instrument';
import { parseDecimal } from '../../shared/lib/decimal';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { Button, Dialog, DialogContent, Field, Input, Switch } from '../../shared/ui';
import { $errors, $formError, $open, closed } from './model';

export function AddInstrumentDialog() {
  const [open, close] = useUnit([$open, closed]);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      {open && <Form />}
    </Dialog>
  );
}

function Form() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [errors, formError, pending, close, create] = useUnit([
    $errors,
    $formError,
    createInstrumentFx.pending,
    closed,
    createInstrumentFx,
  ]);
  const [isin, setIsin] = useState('');
  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  const [signal, setSignal] = useState('');
  const [tick, setTick] = useState(locale === 'bg' ? '0,01' : '0.01');
  const [fractional, setFractional] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const tickValue = parseDecimal(tick, locale);
  const required = (value: string, field: string): string | undefined =>
    errors[field] ?? (attempted && !value.trim() ? t('form.required') : undefined);

  function submit(): void {
    setAttempted(true);
    if (!isin.trim() || !symbol.trim() || !name.trim() || tickValue === null) return;
    void create({
      isin: isin.trim(),
      symbol: symbol.trim(),
      name: name.trim(),
      signal_symbol: signal.trim() || null,
      tick_size: tickValue,
      fractional,
    }).catch(() => undefined);
  }

  const text = (
    field: string,
    label: string,
    value: string,
    set: (v: string) => void,
    hint?: string,
    isRequired = true,
  ) => (
    <Field label={label} hint={hint} error={isRequired ? required(value, field) : errors[field]}>
      {({ id, describedBy, invalid }) => (
        <Input
          id={id}
          value={value}
          autoComplete="off"
          spellCheck={false}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          onChange={(event) => {
            set(event.target.value);
          }}
        />
      )}
    </Field>
  );

  return (
    <DialogContent
      title={t('watchlist.addTitle')}
      description={t('watchlist.addDescription')}
      closeLabel={t('common.close')}
      onSubmit={submit}
      footer={
        <>
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={pending}>
            {t('watchlist.add')}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        {text('isin', t('watchlist.isin'), isin, setIsin, t('watchlist.isinHint'))}
        {text('symbol', t('watchlist.symbol'), symbol, setSymbol, t('watchlist.symbolHint'))}
        {text('name', t('watchlist.name'), name, setName)}
        {text(
          'signal_symbol',
          t('watchlist.signal'),
          signal,
          setSignal,
          t('watchlist.signalHint'),
          false,
        )}
        <Field
          label={t('watchlist.tick')}
          error={
            errors.tick_size ?? (tick && tickValue === null ? t('form.notANumber') : undefined)
          }
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              inputMode="decimal"
              numeric
              unit="€"
              value={tick}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              onChange={(event) => {
                setTick(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('watchlist.fractional')} hint={t('watchlist.fractionalHint')}>
          {({ id, describedBy }) => (
            <div className="flex h-7 items-center">
              <Switch
                id={id}
                aria-describedby={describedBy}
                checked={fractional}
                onCheckedChange={setFractional}
              />
            </div>
          )}
        </Field>
        {formError && <p className="text-xs text-negative">{formError}</p>}
      </div>
    </DialogContent>
  );
}
